'use client';

import { c as _c } from "react/compiler-runtime";
import { DISPATCH_ACTION, PayPalButtons, PayPalButtonsComponentProps, PayPalScriptProvider, ReactPayPalScriptOptions, SCRIPT_LOADING_STATE, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import PayPalLogo from 'assets/images/logos/paypal.svg';
import { Icon, useIsMounted } from 'design-system/client';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import analytics from 'product-analytics';
import { type ReactNode, type RefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useIsFormValid from '../useIsFormValid';
import styles from './PaypalButton.module.css';
import { useMessageContext } from '@/components/Messages';
import { useDonationFormConfig, useDonationFormContext, useRedirectHandler } from '@/donationPages/_dependencies/helpers';
import { report } from '@/errorReporting';
import { ActionFormSubmitErrorTypes } from '@/form/ActionForm/useActionForm/useActionForm';
import { useNetworkStatus } from '@/helpers/hooks';
import type { Maybe } from '@/types';
import { finalizePaymentAction } from '@/donationPages/_dependencies/operations/finalizePaymentAction';
function useFormSubmit(): {
  redirectToRef: RefObject<string | undefined>;
  createBillingAgreement: () => Promise<string>;
  donationIdRef: RefObject<string | undefined>;
  donationTokenRef: RefObject<string | undefined>;
} {
  const t = useTranslations();
  const redirectToRef = useRef<string | undefined>(undefined);
  const donationIdRef = useRef<string | undefined>(undefined);
  const donationTokenRef = useRef<string | undefined>(undefined);
  const {
    addMessage
  } = useMessageContext();
  const {
    submit,
    setBusy
  } = useDonationFormContext();
  const createBillingAgreement = useCallback(async (): Promise<string> => {
    redirectToRef.current = undefined;
    setBusy(true);
    try {
      const {
        data: {
          redirectTo,
          donationId,
          paypalToken,
          donationToken
        }
      } = await submit();
      if (!paypalToken) throw new Error('No paypal token received');
      if (!redirectTo) throw new Error('No redirect url received');
      if (!donationId) throw new Error('No donation ID received');
      if (!donationToken) throw new Error('No donation ID received');
      donationIdRef.current = donationId;
      redirectToRef.current = redirectTo;
      donationTokenRef.current = donationToken;
      return paypalToken;
    } catch (e_) {
      console.error(e_);
      const e = e_ as Maybe<Error>;
      if (e?.message === ActionFormSubmitErrorTypes.ValidationError) {
        throw e;
      }
      addMessage({
        message: t('nextjs.errors.messages.submit'),
        type: 'error'
      });
      report(e);
      setBusy(false);
      return '';
    }
  }, [addMessage, setBusy, submit, t]);
  return {
    createBillingAgreement,
    redirectToRef,
    donationIdRef,
    donationTokenRef
  };
}
function usePaypalOptions(clientId) {
  const $ = _c(2);
  const locale = useLocale();
  const t0 = locale === "de" ? "de_DE" : "en_US";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      clientId: "",
      commit: true,
      currency: "EUR",
      intent: "tokenize",
      disableFunding: "card,sepa",
      vault: true,
      locale: t0,
      dataReactPaypalScriptId: "sdk-paypal-script",
      dataNamespace: "sdk-paypal",
      components: "buttons"
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [options, setOptions] = useState(t1);
  if (clientId && options.clientId !== clientId) {
    setOptions(old => ({
      ...old,
      clientId
    }));
  }
  return options.clientId ? options : undefined;
}
function usePayPalButtonProps() {
  const $ = _c(20);
  const isValid = useIsFormValid();
  const locale = useLocale();
  const {
    debug
  } = useDonationFormConfig();
  const {
    setBusy,
    formState,
    getValues
  } = useDonationFormContext();
  const {
    isSubmitting
  } = formState;
  const redirectHandler = useRedirectHandler();
  const {
    createBillingAgreement,
    redirectToRef,
    donationIdRef,
    donationTokenRef
  } = useFormSubmit();
  const [error, setError] = useState(undefined);
  let t0;
  if ($[0] !== donationIdRef || $[1] !== donationTokenRef || $[2] !== getValues || $[3] !== locale || $[4] !== redirectHandler || $[5] !== redirectToRef) {
    t0 = async () => {
      if (redirectToRef.current && donationIdRef.current && donationTokenRef.current) {
        const {
          error: error_0
        } = await finalizePaymentAction(donationTokenRef.current, locale);
        if (error_0) {
          console.error(error_0.message);
          setError(PayPalFatalError.Other);
        }
        await redirectHandler(redirectToRef.current, donationIdRef.current, getValues());
      }
    };
    $[0] = donationIdRef;
    $[1] = donationTokenRef;
    $[2] = getValues;
    $[3] = locale;
    $[4] = redirectHandler;
    $[5] = redirectToRef;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const onApprove = t0;
  let t1;
  if ($[7] !== setBusy) {
    t1 = () => {
      setBusy(false);
    };
    $[7] = setBusy;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const onCancel = t1;
  let t2;
  if ($[9] !== setBusy) {
    t2 = e_ => {
      setBusy(false);
      const e = e_ as Maybe<Error>;
      if (e?.message === ActionFormSubmitErrorTypes.ValidationError) {
        return;
      }
      setError(PayPalFatalError.Other);
    };
    $[9] = setBusy;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const onError = t2;
  const env = debug ? "sandbox" : "production";
  const t3 = !isValid;
  let t4;
  if ($[11] !== createBillingAgreement || $[12] !== env || $[13] !== error || $[14] !== isSubmitting || $[15] !== onApprove || $[16] !== onCancel || $[17] !== onError || $[18] !== t3) {
    t4 = {
      onCancel,
      onError,
      isSubmitting,
      createBillingAgreement,
      onApprove,
      error,
      env,
      style: buttonStyle,
      disabled: t3
    };
    $[11] = createBillingAgreement;
    $[12] = env;
    $[13] = error;
    $[14] = isSubmitting;
    $[15] = onApprove;
    $[16] = onCancel;
    $[17] = onError;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const props = t4;
  return props;
}
const PaypalButton = memo(function PaypalButton(t0) {
  const $ = _c(7);
  const {
    clientId,
    onClick
  } = t0;
  const options = usePaypalOptions(clientId);
  if (!clientId) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <OtherErrorMessage />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!options) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DummyPayPalButtonWrapper busy={true} />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== onClick) {
    t1 = <PayPalButtonInner onClick={onClick} />;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t1) {
    t2 = <PayPalScriptProvider deferLoading={true} options={options}>{t1}</PayPalScriptProvider>;
    $[4] = options;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});
const PayPalButtonStatus = {
  Initial: 'Initial',
  ReadyToLoad: 'ReadyToLoad',
  FieldError: 'FieldError',
  FatalError: 'FatalError',
  PaypalError: 'PaypalError',
  SDKLoadingError: 'SDKLoadingError',
  Ok: 'Ok',
  Busy: 'Busy',
  Offline: 'Offline'
} as const;
type PayPalButtonStatus = (typeof PayPalButtonStatus)[keyof typeof PayPalButtonStatus];
const PayPalFatalError = {
  LoadingFailed: 'LoadingFailed',
  TooManyRequests: 'TooManyRequests',
  UnsupportedBrowser: 'UnsupportedBrowser',
  SafariTimeout: 'SafariTimeout',
  Other: 'Other'
} as const;
type PayPalFatalError = (typeof PayPalFatalError)[keyof typeof PayPalFatalError];
function useTimeoutError() {
  const $ = _c(12);
  const [t0] = usePayPalScriptReducer();
  const {
    isPending,
    isInitial,
    isResolved,
    isRejected
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isSafari = t1;
  const [hasTimeoutError, setHasTimeoutError] = useState(false);
  const timeoutRef = useRef(null);
  const isPendingRef = useRef(isPending && !(isResolved || isRejected));
  let t2;
  if ($[1] !== isPending || $[2] !== isRejected || $[3] !== isResolved) {
    t2 = () => {
      isPendingRef.current = isPending && !(isResolved || isRejected);
    };
    $[1] = isPending;
    $[2] = isRejected;
    $[3] = isResolved;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isInitial || $[6] !== isPending || $[7] !== isRejected || $[8] !== isResolved) {
    t3 = [isPending, isInitial, isResolved, isRejected];
    $[5] = isInitial;
    $[6] = isPending;
    $[7] = isRejected;
    $[8] = isResolved;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!isSafari) {
        return;
      }
      timeoutRef.current = setTimeout(() => {
        if (!isPendingRef.current) {
          return;
        }
        analytics.donationSafariPaypalLoadingError({
          navigator: navigator.userAgent,
          os: navigator.platform
        });
        setHasTimeoutError(true);
      }, 15000);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    };
    t5 = [isSafari];
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  return hasTimeoutError;
}
function usePayPalStatus(props) {
  const $ = _c(13);
  const [t0] = usePayPalScriptReducer();
  const {
    isResolved,
    isRejected,
    isInitial,
    isPending
  } = t0;
  const {
    formState
  } = useDonationFormContext();
  const {
    isSubmitting
  } = formState;
  const networkStatus = useNetworkStatus();
  const isValid = useIsFormValid();
  const isOffline = networkStatus === "offline";
  const isUnsupportedBrowser = /Twitter/i.test(navigator.userAgent);
  const hasSafariTimeoutError = useTimeoutError();
  let t1;
  bb0: {
    if (!props) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.Initial, undefined];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (props.error) {
      let t2;
      if ($[1] !== props.error) {
        t2 = [PayPalButtonStatus.FatalError, props.error];
        $[1] = props.error;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isUnsupportedBrowser) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.FatalError, PayPalFatalError.UnsupportedBrowser];
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isInitial && isValid) {
      let t2;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.ReadyToLoad, undefined];
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isInitial) {
      let t2;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.Initial, undefined];
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (hasSafariTimeoutError) {
      let t2;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.SDKLoadingError, PayPalFatalError.SafariTimeout];
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isPending || isSubmitting) {
      let t2;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.Busy, undefined];
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (!isValid) {
      let t2;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.FieldError, undefined];
        $[8] = t2;
      } else {
        t2 = $[8];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isOffline) {
      let t2;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.Offline, undefined];
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isRejected) {
      let t2;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.FatalError, PayPalFatalError.LoadingFailed];
        $[10] = t2;
      } else {
        t2 = $[10];
      }
      t1 = t2 as const;
      break bb0;
    }
    if (isResolved) {
      let t2;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [PayPalButtonStatus.Ok, undefined];
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      t1 = t2 as const;
      break bb0;
    }
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [PayPalButtonStatus.Busy, undefined];
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = t2 as const;
  }
  const result = t1;
  return result;
}
function useLoadPayPalWhenReady(status) {
  const $ = _c(4);
  const [, dispatch] = usePayPalScriptReducer();
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== status) {
    t0 = () => {
      if (status !== PayPalButtonStatus.ReadyToLoad) {
        return;
      }
      dispatch({
        type: DISPATCH_ACTION.LOADING_STATUS,
        value: SCRIPT_LOADING_STATE.PENDING
      });
    };
    t1 = [dispatch, status];
    $[0] = dispatch;
    $[1] = status;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function DummyPayPalButtonWrapper(t0) {
  const $ = _c(7);
  const {
    onClick,
    children,
    busy
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      height: buttonHeight
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== busy || $[2] !== onClick) {
    t2 = <DummyPayPalButton busy={busy} onClick={onClick} />;
    $[1] = busy;
    $[2] = onClick;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={styles.container} style={t1}>{t2}{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function PayPalButtonInner(t0) {
  const $ = _c(25);
  const {
    onClick
  } = t0;
  const t1 = usePayPalButtonProps();
  let error;
  let props;
  if ($[0] !== t1) {
    ({
      error,
      ...props
    } = t1);
    $[0] = t1;
    $[1] = error;
    $[2] = props;
  } else {
    error = $[1];
    props = $[2];
  }
  let t2;
  if ($[3] !== error) {
    t2 = {
      error
    };
    $[3] = error;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [status, fatalError] = usePayPalStatus(t2);
  useLoadPayPalWhenReady(status);
  if (status === PayPalButtonStatus.FatalError) {
    let t3;
    if ($[5] !== fatalError) {
      t3 = <FatalErrorMessage error={fatalError} />;
      $[5] = fatalError;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const busy = status === PayPalButtonStatus.Busy;
  const ok = status === PayPalButtonStatus.Ok;
  let t3;
  if ($[7] !== fatalError || $[8] !== status) {
    t3 = status === PayPalButtonStatus.SDKLoadingError && <FatalErrorMessage error={fatalError} />;
    $[7] = fatalError;
    $[8] = status;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== ok) {
    t4 = !ok ? {
      display: "none"
    } : {};
    $[10] = ok;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onClick || $[13] !== props) {
    t5 = <PayPalButtons onClick={onClick} {...props} />;
    $[12] = onClick;
    $[13] = props;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div style={t4}>{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== busy || $[19] !== onClick || $[20] !== t6) {
    t7 = <DummyPayPalButtonWrapper onClick={onClick} busy={busy}>{t6}</DummyPayPalButtonWrapper>;
    $[18] = busy;
    $[19] = onClick;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t7) {
    t8 = <>{t3}{t7}</>;
    $[22] = t3;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function DummyPayPalButton(t0) {
  const $ = _c(17);
  const {
    busy,
    onClick: onClick_
  } = t0;
  const t = useTranslations();
  const isValid = useIsFormValid();
  const disabled = !useIsMounted();
  let t1;
  if ($[0] !== busy || $[1] !== isValid || $[2] !== onClick_) {
    t1 = () => {
      if (busy || !isValid) {
        return;
      }
      onClick_?.();
    };
    $[0] = busy;
    $[1] = isValid;
    $[2] = onClick_;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onClick = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      height: buttonHeight,
      backgroundColor: "transparent"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = disabled || busy;
  let t4;
  if ($[5] !== busy) {
    t4 = busy && <Icon color="bg-light" name="spinner" size="400" />;
    $[5] = busy;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      display: "inline-block"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("nextjs.core.submit");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <Image style={t5} src={PayPalLogo as string} alt={t6} />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onClick || $[13] !== t3 || $[14] !== t4 || $[15] !== t7) {
    t8 = <div style={dummyButtonStyle}><button style={t2} className={styles.dummyPaypalButton} onClick={onClick} disabled={t3} type="submit">{t4}{t7}</button></div>;
    $[12] = onClick;
    $[13] = t3;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function FatalErrorMessage(t0) {
  const $ = _c(5);
  const {
    error
  } = t0;
  if (typeof error === "undefined") {
    return null;
  }
  switch (error) {
    case PayPalFatalError.TooManyRequests:
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Error429Message />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case PayPalFatalError.LoadingFailed:
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <NoPaypalJsMessage />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case PayPalFatalError.UnsupportedBrowser:
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <UnsupportedBrowserMessage />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case PayPalFatalError.SafariTimeout:
      {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <SafariTimeoutMessage />;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <OtherErrorMessage />;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
  }
}
function NoPaypalJsMessage() {
  const $ = _c(4);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("nextjs.donate.errors.paypal_ad_blocker");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className={styles.errorMessage}>{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function Error429Message() {
  const $ = _c(11);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("nextjs.donate.errors.paypal_too_many_requests");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className={styles.errorMessage}>{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("nextjs.donate.errors.paypal_too_many_requests_settings");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className={styles.errorMessage}>{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function OtherErrorMessage() {
  const $ = _c(4);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("nextjs.donate.errors.paypal_other");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className={styles.errorMessage}>{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function UnsupportedBrowserMessage() {
  const $ = _c(4);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("nextjs.donate.errors.paypal_unsupported_browser");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className={styles.errorMessage}>{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SafariTimeoutMessage() {
  const $ = _c(4);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("nextjs.donate.errors.paypal_safari_timeout");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <p className={styles.errorMessage}>{t0}</p>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
const buttonHeight = 50;
const buttonStyle: PayPalButtonsComponentProps['style'] = {
  height: buttonHeight,
  color: 'gold',
  fundingicons: false,
  // does not exist on props thus casting below
  label: 'checkout',
  shape: 'rect',
  size: 'responsive',
  tagline: false
} as PayPalButtonsComponentProps['style'];
const dummyButtonStyle: React.CSSProperties = {
  height: buttonHeight,
  margin: 0,
  textAlign: 'center',
  zIndex: -1,
  position: 'absolute',
  left: 0,
  right: 0
};
export default PaypalButton;